import TrackedEvent from '@/Enums/Creatives/TrackedEvent.enum';
import { type VastParseResult, fetchAndParseVast } from '@/helpers/creatives/vastParser.helpers';
import { type RefObject, useEffect, useRef, useState } from 'react';
import TrackingEventsPanel from './TrackingEventsPanel';
import { useVastPlaybackTracking } from './useVastPlaybackTracking';

interface IProps {
  tagContent: string;
  mediaType: 'video' | 'audio';
}

const VastMediaPlayer = ({ tagContent, mediaType }: IProps) => {
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const [mediaError, setMediaError] = useState(false);
  const [parsed, setParsed] = useState<VastParseResult | null>(null);
  const [loading, setLoading] = useState(true);
  const { firedEvents, fireEvent, remainingSeconds } = useVastPlaybackTracking(mediaRef);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAndParseVast(tagContent).then(result => {
      if (!cancelled) {
        setParsed(result);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [tagContent]);

  if (loading) {
    return <div className='text-gray-500 text-sm p-4'>Loading preview...</div>;
  }

  if (!parsed || parsed.error) {
    return (
      <div className='p-4'>
        <p className='text-gray-500 text-sm mb-3'>{parsed?.error || 'Could not parse VAST XML for preview.'}</p>
        <p className='whitespace-pre-wrap break-words border border-gray-200 rounded-md p-2.5 text-xs text-gray-600 max-h-[300px] overflow-y-auto'>
          {tagContent}
        </p>
      </div>
    );
  }

  if (parsed.isWrapper && !parsed.mediaUrl) {
    return <div className='text-gray-500 text-sm p-4'>Wrapper VAST — preview not available</div>;
  }

  if (!parsed.mediaUrl) {
    return <div className='text-gray-500 text-sm p-4'>No media file found in VAST</div>;
  }

  const isVideo = mediaType === 'video';
  const errorHeight = isVideo ? 'h-[300px]' : 'h-[100px]';

  return (
    <div className='flex gap-6 w-full'>
      <div className='flex-[3] min-w-0'>
        {mediaError ? (
          <div
            className={`flex items-center justify-center ${errorHeight} bg-gray-100 rounded-lg text-gray-500 text-sm`}
          >
            Unable to load {mediaType}. The media URL may be blocked by CORS or unavailable.
          </div>
        ) : isVideo ? (
          <video
            ref={mediaRef as RefObject<HTMLVideoElement>}
            controls
            className='w-full rounded-lg bg-black'
            onError={() => setMediaError(true)}
          >
            <source src={parsed.mediaUrl} />
          </video>
        ) : (
          <audio
            ref={mediaRef as RefObject<HTMLAudioElement>}
            controls
            className='w-full mt-4'
            onError={() => setMediaError(true)}
          >
            <source src={parsed.mediaUrl} />
          </audio>
        )}
      </div>
      <div className='flex-[2] min-w-0'>
        <TrackingEventsPanel
          trackingEvents={parsed.trackingEvents}
          clickThroughUrl={parsed.clickThroughUrl}
          firedEvents={firedEvents}
          remainingSeconds={remainingSeconds}
          onClickFired={() => fireEvent(TrackedEvent.CLICK)}
        />
      </div>
    </div>
  );
};

export default VastMediaPlayer;
